import { alpha, Box, Button, Typography, useTheme } from "@mui/material";
import { FileCard } from "@pages:app/document-translation/components/FileCard";
import { useTranslation } from "react-i18next";
import { MdRefresh } from "react-icons/md";

import { ErrorCardProps } from "../types";

export const ErrorCard = ({
  filename,
  size,
  onRetry,
  onUploadDifferent,
  language,
}: ErrorCardProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 4,
        minHeight: 300,
      }}
    >
      {/* Error Icon with X mark */}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 3,
        }}
      >
        {/* Background Circle */}
        <Box
          sx={{
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #FFB4A9 0%, #FF9F92 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 8px 24px rgba(255, 159, 146, 0.3)",
            animation: "scaleIn 0.5s ease-out",
            "@keyframes scaleIn": {
              "0%": {
                transform: "scale(0)",
                opacity: 0,
              },
              "100%": {
                transform: "scale(1)",
                opacity: 1,
              },
            },
          }}
        >
          {/* X mark SVG */}
          <Box
            component="svg"
            viewBox="0 0 60 60"
            sx={{
              width: "60px",
              height: "60px",
              stroke: "#ffffff",
              strokeWidth: 3,
              strokeLinecap: "round",
              strokeLinejoin: "round",
              fill: "none",
              animation: "drawError 0.5s ease-out 0.3s forwards",
              strokeDasharray: 100,
              strokeDashoffset: 100,
              "@keyframes drawError": {
                to: {
                  strokeDashoffset: 0,
                },
              },
            }}
          >
            <line x1="15" y1="15" x2="45" y2="45" />
            <line x1="45" y1="15" x2="15" y2="45" />
          </Box>
        </Box>
      </Box>

      {/* Error Message */}
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          fontSize: "28px",
          color: (theme) => theme.palette.text.primary,
          textAlign: "center",
          mb: 4,
          animation: "fadeInUp 0.5s ease-out 0.2s both",
          "@keyframes fadeInUp": {
            "0%": {
              opacity: 0,
              transform: "translateY(20px)",
            },
            "100%": {
              opacity: 1,
              transform: "translateY(0)",
            },
          },
        }}
      >
        {t("translation.errorCard.title")}
      </Typography>

      {/* File Card */}
      <FileCard
        size={size}
        filename={filename || t("translation.unknownFile")}
        language={language}
      />

      {/* Try Again Button */}
      <Button
        variant="contained"
        fullWidth
        onClick={onRetry}
        startIcon={<MdRefresh size={20} />}
        sx={{
          maxWidth: 480,
          minHeight: "44px",
          background: theme.palette.primary.main,
          color: "#ffffff",
          fontSize: "16px",
          fontWeight: 600,
          padding: "14px 20px",
          borderRadius: "16px",
          textTransform: "none",
          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
          mb: "16px",
          animation: "fadeInUp 0.5s ease-out 0.6s both",
          "&:hover": {
            background: theme.palette.primary.dark,
            transform: "translateY(-2px)",
            boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
          },
          "&:active": {
            transform: "translateY(0)",
          },
        }}
      >
        {t("common.tryAgain")}
      </Button>

      {/* Upload Different Link */}
      <Typography
        onClick={onUploadDifferent}
        sx={{
          color: "#666468",
          fontSize: "14px",
          textDecoration: "underline",
          cursor: "pointer",
          textAlign: "center",
          animation: "fadeInUp 0.5s ease-out 0.7s both",
          "&:hover": {
            color: "#1D1D1D",
          },
        }}
      >
        {t("translation.errorCard.uploadDifferent")}
      </Typography>
    </Box>
  );
};
